// src/pages/profiles/profile-detail-page/profile-detail/FamilyHealthTab.jsx
import React from "react";
import {
  Grid, Box, Typography, Chip, Stack, Paper, Avatar, useTheme, useMediaQuery
} from "@mui/material";
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import SmokingRoomsIcon from '@mui/icons-material/SmokingRooms';
import LocalDrinkIcon from '@mui/icons-material/LocalDrink';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import SportsIcon from '@mui/icons-material/Sports';
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import GroupsIcon from '@mui/icons-material/Groups';
import PsychologyIcon from '@mui/icons-material/Psychology';
import FavoriteIcon from '@mui/icons-material/Favorite';
import AccessibilityIcon from '@mui/icons-material/Accessibility';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import InfoCard from "./components/InfoCard";

// Reusable components (same pattern as OverviewTab)
const IconBox = ({ icon }) => (
  <Box sx={{
    width: 40, height: 40, borderRadius: 2,
    bgcolor: 'primary.alpha20',
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  }}>
    {React.cloneElement(icon, { sx: { color: 'primary.light' } })}
  </Box>
);

const SmallIconBox = ({ icon }) => (
  <Box sx={{
    width: 24, height: 24, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    bgcolor: 'primary.alpha20', mb: 1, mx: 'auto'
  }}>
    {icon}
  </Box>
);

const InfoRow = ({ icon, label, value }) => (
  <Stack direction="row" spacing={2} alignItems="center" sx={{
    p: 2, borderRadius: 2, bgcolor: 'primary.alpha10',
    border: 1, borderColor: 'divider', mb: 1.5
  }}>
    <IconBox icon={icon} />
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>{label}</Typography>
      <Typography variant="body1" color="text.primary" sx={{ fontWeight: 600 }}>{value || '-'}</Typography>
    </Box>
  </Stack>
);

const Section = ({ title, icon, children, ...props }) => (
  <InfoCard title={title} icon={icon} sx={{
    bgcolor: 'background.default',
    border: 1, borderColor: 'divider',
    height: '100%',
    ...props
  }}>
    {children}
  </InfoCard>
);

// Helper functions
const getDietArray = (profile) => {
  if (!profile?.diet) return [];
  return Array.isArray(profile.diet) ? profile.diet : [profile.diet];
};

const FamilyHealthTab = ({ profile }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dietArray = getDietArray(profile);

  return (
    <Grid container spacing={isMobile ? 2 : 3}>

      {/* Family Details */}
      <Grid item xs={12} md={6}>
        <Section title="Family Details" icon={<FamilyRestroomIcon />} sx={{ minHeight: 540 }}>
          {[
            { icon: <GroupsIcon />, label: "Marital Status", value: profile?.marital_status },
            { icon: <ChildCareIcon />, label: "Children", value: profile?.children_count > 0 ? `${profile.children_count}` : 'None' },
            { icon: <GroupsIcon />, label: "Family Type", value: profile?.family_type },
            { icon: <PsychologyIcon />, label: "Family Status", value: profile?.family_status }
          ].map((item, i) => <InfoRow key={i} {...item} />)}

          {/* Parents Info */}
          {(profile?.father_profession || profile?.mother_profession) && (
            <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'primary.alpha10', border: 1, borderColor: 'divider', mt: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mb: 1, display: 'block' }}>
                Parents' Professions
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {profile?.father_profession && (
                  <Chip 
                    label={`Father: ${profile.father_profession}`} 
                    size="small"
                    sx={{
                      bgcolor: 'primary.alpha20',
                      color: 'primary.light',
                      fontWeight: 500,
                      border: 1,
                      borderColor: 'divider'
                    }}
                  />
                )}
                {profile?.mother_profession && (
                  <Chip 
                    label={`Mother: ${profile.mother_profession}`} 
                    size="small"
                    sx={{
                      bgcolor: 'primary.alpha20',
                      color: 'primary.light',
                      fontWeight: 500,
                      border: 1,
                      borderColor: 'divider'
                    }}
                  />
                )}
              </Box>
            </Paper>
          )}

          {/* Siblings */}
          <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'primary.alpha10', border: 1, borderColor: 'divider', mt: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mb: 1.5, display: 'block' }}>
              Siblings
            </Typography>
            <Grid container spacing={1}>
              {[
                { label: "Total", value: profile?.siblings_total || 0 },
                { label: "Brothers", value: profile?.brothers_total || 0, sub: profile?.brothers_married ? `${profile.brothers_married} married` : '' },
                { label: "Sisters", value: profile?.sisters_total || 0, sub: profile?.sisters_married ? `${profile.sisters_married} married` : '' }
              ].map((item, i) => (
                <Grid item xs={4} key={i}>
                  <Box sx={{ textAlign: 'center', p: 1, borderRadius: 2, bgcolor: 'primary.alpha5' }}>
                    <Typography variant="h4" color="primary.light" sx={{ fontWeight: 700 }}>{item.value}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.label}
                      {item.sub && <Box sx={{ fontSize: '0.7em', color: 'text.secondary' }}>{item.sub}</Box>}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Section>
      </Grid>

      {/* Health & Lifestyle */}
      <Grid item xs={12} md={6}>
        <Section title="Health & Lifestyle" icon={<HealthAndSafetyIcon />} sx={{ minHeight: 540 }}>

          {/* Health Status */}
          <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'primary.alpha10', border: 1, borderColor: 'divider', mb: 1.5 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <IconBox icon={<MedicalServicesIcon />} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>Disability</Typography>
                <Typography variant="body1" color="text.primary" sx={{ fontWeight: 600 }}>{profile?.disability || 'None'}</Typography>
              </Box>
              <Chip
                icon={profile?.disability === "None" ? <CheckCircleIcon /> : <CancelIcon />}
                label={profile?.disability === "None" ? "None" : "Present"}
                size="small"
                sx={{
                  bgcolor: profile?.disability === "None" ? 'success.alpha20' : 'error.alpha20',
                  color: profile?.disability === "None" ? 'success.main' : 'error.main',
                  fontWeight: 600,
                  border: 1,
                  borderColor: 'divider'
                }}
              />
            </Stack>
            {profile?.medical_conditions && profile.medical_conditions !== "None" && (
              <Box sx={{ pl: 7, mt: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>Medical Conditions</Typography>
                <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>{profile.medical_conditions}</Typography>
              </Box>
            )}
          </Paper>

          {/* Habits */}
          <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'primary.alpha10', border: 1, borderColor: 'divider', mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mb: 1, display: 'block' }}>Habits</Typography>
            <Grid container spacing={1}>
              {[
                { icon: <SmokingRoomsIcon />, label: "Smoking", value: profile?.smoking },
                { icon: <LocalDrinkIcon />, label: "Drinking", value: profile?.drinking }
              ].map((item, i) => (
                <Grid item xs={6} key={i}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, borderRadius: 2, bgcolor: 'primary.alpha5' }}>
                    <Box sx={{ color: 'primary.light' }}>{item.icon}</Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                      <Typography variant="body1" color="text.primary" sx={{ fontWeight: 600 }}>{item.value || '-'}</Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>

          {/* Diet & Interests */}
          <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'primary.alpha10', border: 1, borderColor: 'divider' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5 }}>Diet Preferences</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {dietArray.length > 0 ? dietArray.map((item, i) => (
                    <Chip 
                      key={i} 
                      label={item} 
                      icon={<RestaurantIcon />} 
                      size="small"
                      sx={{
                        bgcolor: 'primary.alpha20',
                        color: 'primary.light',
                        fontWeight: 500,
                        border: 1,
                        borderColor: 'divider'
                      }}
                    />
                  )) : <Typography variant="body2" color="text.secondary" fontStyle="italic">Not specified</Typography>}
                </Box>
              </Grid>

              {profile?.sports_interest?.length > 0 && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5 }}>Sports Interest</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {profile.sports_interest.map((item, i) => (
                      <Chip 
                        key={i} 
                        label={item} 
                        icon={<SportsIcon />} 
                        size="small"
                        sx={{
                          bgcolor: 'primary.alpha20',
                          color: 'primary.light',
                          fontWeight: 500,
                          border: 1,
                          borderColor: 'divider'
                        }}
                      />
                    ))}
                  </Box>
                </Grid>
              )}

              {profile?.hobbies?.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5 }}>Hobbies</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {profile.hobbies.map((item, i) => (
                      <Chip 
                        key={i} 
                        label={item} 
                        size="small"
                        sx={{
                          bgcolor: 'primary.alpha20',
                          color: 'primary.light',
                          fontWeight: 500,
                          border: 1,
                          borderColor: 'divider'
                        }}
                      />
                    ))}
                  </Box>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Section>
      </Grid>

      {/* Personal Description */}
      <Grid item xs={12}>
        <Section title="Personal Description" icon={<EmojiPeopleIcon />} sx={{ minHeight: 250 }}>
          <Paper sx={{ p: 3, bgcolor: 'primary.alpha5', borderRadius: 2, border: 1, borderColor: 'divider', minHeight: 150 }}>
            <Typography variant="body1" sx={{
              lineHeight: 1.8,
              fontStyle: profile?.description ? 'normal' : 'italic',
              color: profile?.description ? 'text.primary' : 'text.secondary'
            }}>
              {profile?.description || 'No description provided'}
            </Typography>
          </Paper>
        </Section>
      </Grid>

      {/* Summary */}
      <Grid item xs={12}>
        <Paper sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 3,
          bgcolor: 'primary.alpha10',
          border: 1, borderColor: 'divider'
        }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: 'primary.alpha20', color: 'primary.light', mr: 2 }}>
                  <FavoriteIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={700} color="text.primary">
                    Family & Health
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Summary
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
              <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.5,
                justifyContent: { xs: 'center', md: 'flex-end' }
              }}>
                {[
                  { icon: <FamilyRestroomIcon />, label: profile?.family_type },
                  { icon: <SmokingRoomsIcon />, label: profile?.smoking ? `Smoking: ${profile.smoking}` : null },
                  { icon: <LocalDrinkIcon />, label: profile?.drinking ? `Drinking: ${profile.drinking}` : null },
                  { icon: <RestaurantIcon />, label: dietArray.length > 0 ? `Diet: ${dietArray.slice(0, 2).join(', ')}${dietArray.length > 2 ? '...' : ''}` : null },
                  { icon: <ChildCareIcon />, label: profile?.children_count > 0 ? `${profile.children_count} ${profile.children_count === 1 ? 'child' : 'children'}` : null },
                  { icon: <AccessibilityIcon />, label: profile?.disability && profile.disability !== "None" ? "Special Needs" : null },
                  { icon: <GroupsIcon />, label: profile?.siblings_total > 0 ? `${profile.siblings_total} ${profile.siblings_total === 1 ? 'sibling' : 'siblings'}` : null }
                ].filter(i => i.label).map((item, i) => (
                  <Chip
                    key={i}
                    label={item.label}
                    icon={item.icon}
                    size="small"
                    sx={{
                      bgcolor: 'primary.alpha20',
                      color: 'primary.light',
                      fontWeight: 600,
                      border: 1,
                      borderColor: 'divider'
                    }}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default FamilyHealthTab;